import { NavigateBackLink } from '@exam-center/components';
import {
  AdminExamRoomRequestInfoProvider,
  useAdminExamRoomRequestInfo,
} from '@exam-center/exam-location-management';
import { ExamLocationRequestWorkflowStepCode } from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import { Navigate, useParams } from '@exam-center/router';
import { Box, Flex, Label, LabelColor, PageHeader, Text } from '@shared/components';
import { PermissionGuard } from '@shared/permissions';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Outlet } from 'react-router-dom';

const STATUS_CONFIG: Record<
  ExamLocationRequestWorkflowStepCode,
  { label: string; color: LabelColor }
> = {
  [ExamLocationRequestWorkflowStepCode.ExamLocationRequestApproved]: {
    label: 'ผ่านการอนุมัติ',
    color: LabelColor.SUCCESS,
  },
  [ExamLocationRequestWorkflowStepCode.ExamLocationRequestRejected]: {
    label: 'ไม่ผ่านการอนุมัติ',
    color: LabelColor.ERROR,
  },
  [ExamLocationRequestWorkflowStepCode.ExamLocationRequestInReview_1]: {
    label: 'คำขอที่รออนุมัติ',
    color: LabelColor.WARNING,
  },
  [ExamLocationRequestWorkflowStepCode.ExamLocationRequestInReview_2]: {
    label: 'คำขอที่รออนุมัติ',
    color: LabelColor.WARNING,
  },
};

function AdminExamLocationRequestHeader() {
  const { examLocationRequestInfo, examRoomRequestInfo } = useAdminExamRoomRequestInfo();

  const title =
    examLocationRequestInfo?.nameTh ||
    examLocationRequestInfo?.nameEn ||
    examLocationRequestInfo?.buildingName ||
    'รายละเอียดคำขอสถานที่สอบ';

  const verdicts = examRoomRequestInfo.examLocationRequestVerdicts;

  const savedVerdict = useMemo(() => {
    if (verdicts.length === 0) {
      return null;
    }

    return verdicts.find((verdict) => verdict.submittedAt === null);
  }, [verdicts]);

  const editedBy = savedVerdict?.updatedUser
    ? `${savedVerdict.updatedUser.firstname} ${savedVerdict.updatedUser.lastname}`
    : '-';
  const editedAt = savedVerdict?.updatedAt;

  return (
    <PageHeader
      title={title}
      linkAdornment={<NavigateBackLink to='/admin/exam-location/requests' label='Back' />}
      endAdornment={
        examRoomRequestInfo.step ? (
          <Label color={STATUS_CONFIG[examRoomRequestInfo.step].color}>
            {STATUS_CONFIG[examRoomRequestInfo.step].label}
          </Label>
        ) : null
      }
      subtitleSection={
        <Flex sx={{ width: '100%', justifyContent: 'space-between' }}>
          <Flex sx={{ gap: (theme) => theme.spacing(4) }}>
            <Text variant='body2'>เลขที่คำขอ: {examRoomRequestInfo.taskCode ?? '-'}</Text>
            <Text variant='body2'>ศูนย์ทดสอบ: {examRoomRequestInfo.examCenterCode}</Text>
            <Text variant='body2'>
              วันที่ส่งคำขอ:{' '}
              {examRoomRequestInfo.requestedAt
                ? dayjs(examRoomRequestInfo.requestedAt).format('DD-MM-YYYY, HH:mm')
                : '-'}
            </Text>
          </Flex>
          <Flex sx={{ gap: (theme) => theme.spacing(4) }}>
            <Text variant='body2'>
              Last Updated at: {editedAt ? dayjs(editedAt).format('DD-MM-YYYY, HH:mm') : '-'}
            </Text>
            <Text variant='body2'>By: {editedBy}</Text>
          </Flex>
        </Flex>
      }
    />
  );
}

export default function ExamRoomRequestLayout() {
  const { roomRequestId } = useParams('/admin/exam-location/requests/:roomRequestId');

  if (!roomRequestId) {
    return <Navigate to='/admin/exam-location/locations' replace />;
  }

  return (
    <PermissionGuard
      requiredPermission={[
        APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.VIEW,
        APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST_FIRST_TIER_REVIEW.VIEW,
        APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST_SECOND_TIER_REVIEW
          .VIEW,
      ]}
      options={{
        operator: 'OR',
      }}
    >
      <AdminExamRoomRequestInfoProvider roomRequestId={roomRequestId}>
        <AdminExamLocationRequestHeader />
        <Box
          sx={{
            p: (theme) => theme.spacing(6),
            flex: 1,
            gap: (theme) => theme.spacing(6),
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Outlet />
        </Box>
      </AdminExamRoomRequestInfoProvider>
    </PermissionGuard>
  );
}
